<script lang="ts" setup>
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import type { MemberPageConfig } from '../configs/types'
import { useUserStore } from '@/stores'
import { usePurchase, useSubscription } from '@/composables/payment'
import { useElementClick } from '@/composables/tracks/useElementClick'
import { VIP_TYPE } from '@/constants'
import { resources as brandResources, theme } from '@/config/brands'

interface Props {
  config: MemberPageConfig
}

const props = defineProps<Props>()
const router = useRouter()
const route = useRoute()

const userStore = useUserStore()
const { elementClick } = useElementClick()
const { subscribeProducts } = useSubscription('member')
const { templateProducts, loading } = usePurchase('member')

const topupShow = computed(() => templateProducts.value && templateProducts.value.length > 0)

function navigateTo(name: string) {
  if (route.name !== name)
    router.push({ name })
}

function handleTopupOrWelfare() {
  navigateTo(topupShow.value ? 'pay' : 'welfare')
}

const menuItems = computed(() => [
  {
    key: 'welfare',
    label: 'Rewards',
    iconBg: 'rgba(255, 107, 107, 0.12)',
    iconColor: '#FF8A8A',
    icon: 'gift',
    onClick: () => navigateTo('welfare'),
  },
  {
    key: 'history',
    label: 'Library',
    iconBg: 'rgba(78, 205, 196, 0.12)',
    iconColor: '#6EDDD4',
    icon: 'bookmark',
    onClick: () => navigateTo('history'),
  },
  {
    key: 'recharge',
    label: props.config.texts.transactionHistory,
    iconBg: 'rgba(102, 126, 234, 0.12)',
    iconColor: '#8A9CF0',
    icon: 'transaction',
    onClick: () => navigateTo('recharge'),
  },
  {
    key: 'consume',
    label: props.config.texts.chaptersUnlocked,
    iconBg: 'rgba(240, 147, 251, 0.12)',
    iconColor: '#F5A8F0',
    icon: 'unlock',
    onClick: () => navigateTo('consume'),
  },
  {
    key: 'contact-us',
    label: props.config.texts.contactUs,
    iconBg: 'rgba(79, 172, 254, 0.12)',
    iconColor: '#7AC2FE',
    icon: 'customer',
    onClick: () => {
      elementClick('contact_us')
      navigateTo('contact-us')
    },
  },
  {
    key: 'setting',
    label: props.config.texts.setting,
    iconBg: 'rgba(67, 233, 123, 0.12)',
    iconColor: '#6EEFA0',
    icon: 'setting',
    onClick: () => {
      elementClick('setting')
      navigateTo('setting')
    },
  },
])

const avatarBorderColor = props.config.theme.avatarBorder
const userDefaultAvatar = props.config.resources.userDefaultAvatar
const vipBannerBg = props.config.theme.subscribeBg || 'linear-gradient(100deg, #FFF8FB 0%, #FDEAF2 58%, #F7E0EC 100%)'
const vipTitleColor = props.config.theme.subscribeText || '#A95E81'
const vipSubtitleColor = props.config.theme.subscribeText2 || 'rgba(169, 94, 129, 0.58)'

const bgGlobal = theme.bgGlobal
const bgCard = theme.bgCard
const borderPrimary = theme.borderPrimary
const textPrimary = theme.textPrimary
const textTertiary = theme.textTertiary
</script>

<template>
  <div class="page-container">
    <div class="user-section">
      <div class="flex items-center space-x-10px min-w-0">
        <img
          v-if="userStore.$state.headImageUrl"
          class="w-48px h-48px rounded-full avatar-border object-cover"
          :src="userStore.$state.thirdHeadImgUrl"
          alt="avatar"
        >
        <img
          v-else
          class="w-48px h-48px rounded-full object-cover"
          :src="userDefaultAvatar"
          alt="avatar"
        >
        <div class="min-w-0">
          <p class="username">
            {{ userStore.$state.thirdNickName || userStore.$state.email || userStore.$state.memberName }}
          </p>
          <p v-if="userStore.$state.code" class="uid">
            UID {{ userStore.$state.code }}
          </p>
          <p v-if="userStore.$state.subscribe" class="flex items-center mt-4px">
            <img
              v-if="userStore.$state.subscribeType === VIP_TYPE.DISCOUNT"
              :src="config.resources.memberVipDiscount"
              class="w-40px h-13px"
            >
            <img v-else :src="config.resources.memberVip" class="w-40px h-13px">
          </p>
        </div>
      </div>
    </div>

    <div class="coin-card">
      <div class="flex items-center min-w-0">
        <div class="coin-icon-wrap">
          <img :src="brandResources?.coin || config.resources.memberVip" class="w-20px h-20px" alt="coin">
        </div>
        <strong class="coin-value">{{ userStore.$state.coinBalance }}</strong>
      </div>
      <div v-if="!loading" class="shrink-0 ml-10px">
        <div class="topup-btn" @click="elementClick('TopUp'); handleTopupOrWelfare()">
          {{ topupShow ? config.texts.topUp : config.texts.get }}
        </div>
      </div>
    </div>

    <div v-if="subscribeProducts?.length || userStore.$state.subscribe" class="vip-banner">
      <div class="flex-1 min-w-0">
        <div class="vip-title">
          <span v-if="!userStore.$state.subscribe">{{ config.texts.getVip }}</span>
          <span v-else>{{ config.texts.vipMembership }}</span>
        </div>
        <div v-if="!userStore.$state.subscribe" class="vip-subtitle">
          {{ config.texts.moreCoinsMoreBenefits }}
        </div>
        <div v-if="userStore.$state.subscribeEndDate" class="vip-subtitle">
          {{ config.texts.expires }} {{ $d(new Date(userStore.$state.subscribeEndDate), 'short') }}
        </div>
      </div>
      <div v-if="!userStore.$state.subscribe" class="shrink-0 ml-10px">
        <div class="vip-btn" @click="elementClick('Details'); navigateTo('subscribe')">
          {{ config.texts.details }}
        </div>
      </div>
    </div>

    <div class="menu-card storlyn-menu-card">
      <div class="storlyn-menu-grid">
        <button
          v-for="item in menuItems"
          :key="item.key"
          type="button"
          class="storlyn-menu-item"
          @click="item.onClick"
        >
          <div class="menu-icon storlyn-menu-icon" :style="{ background: item.iconBg, color: item.iconColor }">
            <i-icon-park-outline-gift v-if="item.icon === 'gift'" class="text-16px" />
            <i-icon-park-outline-bookmark v-else-if="item.icon === 'bookmark'" class="text-16px" />
            <i-icon-park-outline-transaction v-else-if="item.icon === 'transaction'" class="text-16px" />
            <i-icon-park-outline-unlock v-else-if="item.icon === 'unlock'" class="text-16px" />
            <i-icon-park-outline-customer v-else-if="item.icon === 'customer'" class="text-16px" />
            <i-icon-park-outline-setting-one v-else class="text-16px" />
          </div>
          <span class="storlyn-menu-label">{{ item.label }}</span>
        </button>
      </div>
    </div>

    <div class="h-20px" />
  </div>
</template>

<style scoped>
.page-container {
  width: 100%;
  min-height: 100vh;
  background: v-bind(bgGlobal);
  padding: 8px 12px 16px;
  box-sizing: border-box;
}

.user-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0 20px;
}

.avatar-border {
  border: 2px solid v-bind(avatarBorderColor);
}

.username {
  font-weight: 600;
  font-size: 16px;
  color: v-bind(textPrimary);
  line-height: 22px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 180px;
}

.uid {
  font-size: 11px;
  color: v-bind(textTertiary);
  line-height: 15px;
  margin-top: 2px;
}

.coin-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: v-bind(bgCard);
  border: 1px solid v-bind(borderPrimary);
  border-radius: 16px;
  padding: 14px;
  margin-bottom: 12px;
}

.coin-icon-wrap {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  flex-shrink: 0;
}

.coin-value {
  font-size: 22px;
  font-weight: 700;
  color: v-bind(textPrimary);
  letter-spacing: -0.5px;
}

.topup-btn,
.vip-btn {
  width: 80px;
  height: 32px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  border-radius: 16px;
  cursor: pointer;
  white-space: nowrap;
}

.topup-btn {
  color: #FFFFFF !important;
  background: linear-gradient(90deg, var(--rr-primary-start, #FF81B0) 0%, var(--rr-primary-strong, #EE3BA4) 100%);
}

.vip-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: v-bind(vipBannerBg);
  border: 1px solid v-bind(borderPrimary);
  border-radius: 16px;
  padding: 14px;
  margin-bottom: 12px;
}

.vip-title {
  font-size: 15px;
  font-weight: 600;
  color: v-bind(vipTitleColor);
  line-height: 20px;
}

.vip-subtitle {
  font-size: 12px;
  color: v-bind(vipSubtitleColor);
  line-height: 16px;
  margin-top: 3px;
}

.vip-btn {
  color: #FFFFFF !important;
  background: linear-gradient(90deg, var(--rr-primary-start, #FF81B0) 0%, var(--rr-primary-strong, #EE3BA4) 100%);
}

.menu-card {
  background: v-bind(bgCard);
  border: 1px solid v-bind(borderPrimary);
  border-radius: 16px;
  margin-bottom: 12px;
}

.storlyn-menu-card {
  padding: 14px 12px;
}

.storlyn-menu-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px 8px;
}

.storlyn-menu-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  padding: 10px 4px;
  background: transparent;
  border: 0;
}

.menu-icon {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.storlyn-menu-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
}

.storlyn-menu-label {
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  color: v-bind(textPrimary);
  text-align: center;
}
</style>
